(() => {
  function pushCartGiftEvent(eventName, params) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'custom_event',
      event_name: eventName,
      parameter: JSON.stringify(params)
    });
  }

  function localeRoot() {
    return typeof currentLocal !== 'undefined' ? currentLocal || '' : '';
  }

  function sectionsUrl() {
    return typeof LOCALE_ROOT_URL !== 'undefined' ? LOCALE_ROOT_URL : '/';
  }

  function formatCents(cents) {
    if (typeof window.formatMoneyAmount === 'function')
      return window.formatMoneyAmount(Number(cents) / 100);
    const symbol = window.CART_CURRENCY_SYMBOL || '';
    return `${symbol}${(Number(cents) / 100).toFixed(2)}`;
  }

  function fillTemplate(str, map) {
    if (!str) return '';
    return Object.keys(map).reduce(
      (acc, key) =>
        acc.replace(
          new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'),
          () => map[key]
        ),
      str
    );
  }

  function decodeEntities(str) {
    if (!str || str.indexOf('&') === -1) return str || '';
    const decoderElement = document.createElement('textarea');
    decoderElement.innerHTML = str;
    return decoderElement.value;
  }

  const GIFT_ATTRIBUTE_KEYS = [
    'free_gift_config',
    'free_gift_markets',
    'free_gift_choice_tier_1',
    'free_gift_choice_tier_2',
    'free_gift_dismissed_tier_1',
    'free_gift_dismissed_tier_2'
  ];
  const AB_CHANGE_EVENT = 'cart-gifts-ab:change';
  const GIFT_MARKET_SESSION_KEY = 'cart_gift_last_market';
  const GIFT_MARKET_WARNING_KEY = 'cart_gift_market_warning';
  const MARKET_WARNING_SEEN_DWELL_MS = 500;
  const MARKET_WARNING_EVENTS = [
    'cart:rerendered',
    'cart:updated',
    'cart:discount:applied',
    'cart:discount:removed'
  ];

  class CartGiftController extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;

      const config = this._readConfig();

      if (!config) {
        return;
      }

      this._initialized = true;
      this._config = config;
      this._active = false;
      this._inactive = false;
      this._purging = null;
      this._reconciling = null;
      this._abTransition = Promise.resolve();
      this._pendingRemove = null;

      this._onDocumentClick = this._onDocumentClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      document.addEventListener('click', this._onDocumentClick);
      document.addEventListener('keydown', this._onKeydown);

      this._decision = this._init();
    }

    async _init() {
      const config = this._config;
      const hasTiers =
        !!config.enabled &&
        Array.isArray(config.tiers) &&
        !!config.tiers.length;
      const abState = await this._abReady();
      this._abState = abState;

      if (hasTiers) {
        this._onAbChange = event => {
          this._abState = event.detail;
          this._abTransition = this._abTransition.then(() =>
            this._followAbState()
          );
        };
        document.addEventListener(AB_CHANGE_EVENT, this._onAbChange);
      }

      if (hasTiers && abState?.isB) {
        this._initMarketWarning(config.market, true);
        this._activate();
        return;
      }

      this._inactive = true;
      this._initMarketWarning(config.market, false);
      if (config.hasGiftState) this._purgeAllGiftLines();
    }

    async _followAbState() {
      try {
        if (this._abState?.isB) {
          if (this._active) return;
          await this._purging;
          if (this._active) return;
          this._rememberCurrentMarket(this._config.market, true);
          this._activate();
          return;
        }
        if (!this._active) return;
        this._deactivate();
        await this._reconciling;
        await this._purging;
        await this._purgeAllGiftLines();
      } catch {}
    }

    async _abReady() {
      const abApi = window.cartGiftsUsaAb;
      if (!abApi) return null;
      try {
        await abApi.ready;
      } catch {}
      return abApi;
    }

    _activate() {
      if (this._active) return;
      this._active = true;
      this._inactive = false;
      this._dirty = false;
      this._debounceTimer = null;
      this._domSyncTimer = null;
      this._suppressGiftAddEvent = null;
      this._suppressGiftAddEventTimer = null;

      this._onCartEvent = () => this._scheduleReconcile();
      MARKET_WARNING_EVENTS.forEach(name =>
        document.addEventListener(name, this._onCartEvent)
      );

      this._observeDom();
      this._scheduleReconcile(0);

      const reconcileAfterGb = () => this._scheduleReconcile();
      window.gbInitPromise?.then(reconcileAfterGb, reconcileAfterGb);
    }

    _deactivate() {
      this._active = false;
      this._inactive = true;
      MARKET_WARNING_EVENTS.forEach(name =>
        document.removeEventListener(name, this._onCartEvent)
      );
      this._domObserver?.disconnect();
      this._domObserver = null;
      clearTimeout(this._debounceTimer);
      clearTimeout(this._domSyncTimer);
      clearTimeout(this._suppressGiftAddEventTimer);
      this._suppressGiftAddEvent = null;
      const waiters = this._reconcileWaiters || [];
      this._reconcileWaiters = [];
      waiters.forEach(waiter => waiter());
      this._closeAllDropdowns();
      this._closeRemovePopup();
    }

    _readConfig() {
      const configElement = document.getElementById('cart-gift-config');
      if (!configElement) {
        return null;
      }
      try {
        return JSON.parse(configElement.textContent);
      } catch (err) {
        return null;
      }
    }

    _observeDom() {
      const roots = [
        document.querySelector('.slide-cart-modal'),
        document.getElementById('main-cart-items')
      ].filter(Boolean);

      if (roots.length) {
        const observer = new MutationObserver(() => {
          clearTimeout(this._domSyncTimer);
          this._domSyncTimer = setTimeout(
            () => this._populateAllDropdowns(),
            30
          );
        });
        roots.forEach(root => {
          observer.observe(root, { childList: true, subtree: true });
        });
        this._domObserver = observer;
      }
      this._populateAllDropdowns();
    }

    _populateAllDropdowns() {
      document
        .querySelectorAll('[data-cart-gift-dropdown]')
        .forEach(dropdown => {
          const tier = this._config.tiers.find(
            candidateTier => candidateTier.key === dropdown.dataset.tier
          );
          if (!tier) return;
          const list = dropdown.querySelector('[data-cart-gift-dropdown-list]');
          if (!list) return;
          const card = dropdown.closest('[data-cart-gift-card]');
          const currentVariantId = Number(card ? card.dataset.variantId : NaN);
          const marker = String(currentVariantId);
          if (dropdown.dataset.cartGiftPopulated === marker) return;

          const available = tier.options.filter(option => option.available);
          const unavailable = tier.options.filter(option => !option.available);
          const ordered = available.concat(unavailable);
          const isCartPage = !!dropdown.closest('#main-cart-items');

          list.innerHTML = '';
          let currentLabel = '';
          ordered.forEach(option => {
            const optionLabel = option.label || option.title;
            const li = document.createElement('li');
            li.className = isCartPage
              ? 'cart-gift-dropdown__option cart-gift-dropdown__option--cart'
              : 'cart-gift-dropdown__option';
            li.dataset.cartGiftDropdownOption = '';
            li.dataset.variantId = String(option.variantId);
            li.setAttribute('role', 'option');
            li.textContent = optionLabel;
            if (!option.available) {
              li.classList.add('is-disabled');
              li.setAttribute('aria-disabled', 'true');
            }
            if (option.variantId === currentVariantId) {
              li.classList.add('is-selected');
              li.setAttribute('aria-selected', 'true');
              currentLabel = optionLabel;
            }
            list.appendChild(li);
          });

          if (currentLabel) {
            const valueEl = dropdown.querySelector(
              '[data-cart-gift-dropdown-value]'
            );
            if (valueEl) valueEl.textContent = currentLabel;
          }
          dropdown.dataset.cartGiftPopulated = marker;
        });
    }

    _onDocumentClick(e) {
      const infoBtn = e.target.closest('[data-cart-gift-info]');
      if (infoBtn) {
        e.preventDefault();
        this._toggleInfoTooltip(infoBtn);
        return;
      }
      if (!e.target.closest('.cart-gift-item__info-tooltip')) {
        this._hideAllInfoTooltips();
      }

      if (!this._active) {
        const giftAction = e.target.closest(
          '[data-cart-gift-remove], [data-cart-gift-restore], [data-cart-gift-dropdown-toggle], [data-cart-gift-dropdown-option]'
        );
        if (!giftAction) return;
        e.preventDefault();
        if (this._inactive) {
          this._purgeAllGiftLines();
          return;
        }
        const { target } = e;
        this._pendingClickTarget = target;
        const replay = () => {
          if (this._pendingClickTarget !== target) return;
          this._pendingClickTarget = null;
          if (!target.isConnected || (!this._active && !this._inactive)) return;
          this._onDocumentClick({ target, preventDefault: () => {} });
        };
        this._decision.then(replay, replay);
        return;
      }

      const dropdownToggle = e.target.closest(
        '[data-cart-gift-dropdown-toggle]'
      );
      if (dropdownToggle) {
        e.preventDefault();
        this._toggleDropdown(
          dropdownToggle.closest('[data-cart-gift-dropdown]')
        );
        return;
      }

      const dropdownOption = e.target.closest(
        '[data-cart-gift-dropdown-option]'
      );
      if (dropdownOption) {
        this._onDropdownSelect(dropdownOption);
        return;
      }

      const removeBtn = e.target.closest('[data-cart-gift-remove]');
      if (removeBtn) {
        const card = removeBtn.closest('[data-cart-gift-card]');
        this._openRemovePopup(
          removeBtn.dataset.tier,
          removeBtn.dataset.lineKey,
          card
        );
        return;
      }

      const restoreBtn = e.target.closest('[data-cart-gift-restore]');
      if (restoreBtn) {
        this._restoreGift(restoreBtn.dataset.tier);
        return;
      }

      const confirmBtn = e.target.closest(
        '[data-cart-gift-remove-popup-confirm]'
      );
      if (confirmBtn) {
        this._confirmRemove();
        return;
      }

      const dismissTarget = e.target.closest(
        '[data-cart-gift-remove-popup-cancel], [data-cart-gift-remove-popup-close], [data-cart-gift-remove-popup-backdrop]'
      );
      if (dismissTarget) {
        this._closeRemovePopup();
        return;
      }

      if (!e.target.closest('[data-cart-gift-dropdown]')) {
        this._closeAllDropdowns();
      }
    }

    _onKeydown(e) {
      if (e.key === 'Escape') {
        this._closeAllDropdowns();
        this._closeRemovePopup();
      }
    }

    _toggleInfoTooltip(btn) {
      const tooltip = btn.querySelector('.cart-gift-item__info-tooltip');
      if (!tooltip) return;
      const willOpen = !tooltip.classList.contains('is-open');
      this._hideAllInfoTooltips();
      if (!willOpen) return;

      tooltip.classList.add('is-open');

      if (window.matchMedia('(min-width: 1024px)').matches) return;

      const TOOLTIP_MARGIN = 16;
      const btnRect = btn.getBoundingClientRect();
      tooltip.style.position = 'fixed';
      tooltip.style.top = `${btnRect.bottom + 8}px`;
      tooltip.style.left = '0px';
      const tooltipWidth = tooltip.getBoundingClientRect().width;
      const maxLeft = window.innerWidth - tooltipWidth - TOOLTIP_MARGIN;
      const left = Math.max(TOOLTIP_MARGIN, Math.min(btnRect.left, maxLeft));
      tooltip.style.left = `${left}px`;
      tooltip.style.setProperty(
        '--cart-gift-tooltip-arrow-left',
        `${btnRect.left + btnRect.width / 2 - left}px`
      );
    }

    _hideAllInfoTooltips() {
      document
        .querySelectorAll('.cart-gift-item__info-tooltip.is-open')
        .forEach(tooltip => {
          tooltip.classList.remove('is-open');
          tooltip.style.position = '';
          tooltip.style.top = '';
          tooltip.style.left = '';
          tooltip.style.removeProperty('--cart-gift-tooltip-arrow-left');
        });
    }

    _toggleDropdown(dropdown) {
      if (!dropdown) return;
      const willOpen = !dropdown.classList.contains('is-open');
      this._closeAllDropdowns();
      if (!willOpen) return;
      dropdown.classList.add('is-open');
      const toggle = dropdown.querySelector('[data-cart-gift-dropdown-toggle]');
      const list = dropdown.querySelector('[data-cart-gift-dropdown-list]');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      if (list) list.hidden = false;
    }

    _closeAllDropdowns() {
      document
        .querySelectorAll('[data-cart-gift-dropdown].is-open')
        .forEach(dropdown => {
          dropdown.classList.remove('is-open');
          const toggle = dropdown.querySelector(
            '[data-cart-gift-dropdown-toggle]'
          );
          const list = dropdown.querySelector('[data-cart-gift-dropdown-list]');
          if (toggle) toggle.setAttribute('aria-expanded', 'false');
          if (list) list.hidden = true;
        });
    }

    async _onDropdownSelect(optionEl) {
      if (optionEl.classList.contains('is-disabled')) return;
      const dropdown = optionEl.closest('[data-cart-gift-dropdown]');
      if (!dropdown) return;
      const tierKey = dropdown.dataset.tier;
      const tier = this._config.tiers.find(
        candidateTier => candidateTier.key === tierKey
      );
      if (!tier) return;

      const variantId = Number(optionEl.dataset.variantId);
      const option = tier.options.find(
        candidateOption => candidateOption.variantId === variantId
      );
      if (!option) return;

      const card = dropdown.closest('[data-cart-gift-card]');
      const fromVariantId = Number(card ? card.dataset.variantId : NaN);
      const fromOption = tier.options.find(
        candidateOption => candidateOption.variantId === fromVariantId
      );

      const valueEl = dropdown.querySelector('[data-cart-gift-dropdown-value]');
      if (valueEl) valueEl.textContent = option.label || option.title;
      this._closeAllDropdowns();

      if (fromOption && fromOption.variantId === variantId) return;

      await this._setAttributes({
        [`free_gift_choice_${tierKey}`]: option.handle
      });
      this._armSuppressGiftAddEvent(tierKey, option.variantId);
      await this._scheduleReconcile(0);

      const isSameProduct =
        fromOption && fromOption.productId === option.productId;
      const isRelatedProduct =
        fromOption &&
        fromOption.groupId &&
        option.groupId &&
        fromOption.groupId === option.groupId;
      const hasColorLabel = giftOption =>
        Boolean(giftOption.label) && giftOption.label !== giftOption.title;
      const isTierColorSwitch =
        fromOption && hasColorLabel(fromOption) && hasColorLabel(option);
      const isColorChange =
        isSameProduct || isRelatedProduct || isTierColorSwitch;
      const surface = this._currentSurface();
      pushCartGiftEvent(
        isColorChange ? 'product_color_change' : 'product_variant_change',
        {
          from: fromOption
            ? isColorChange
              ? fromOption.label
              : fromOption.title
            : '',
          value: isColorChange ? option.label : option.title,
          content_url: option.handle,
          type: isColorChange && surface === 'cart_page' ? 'cart' : surface
        }
      );
    }

    _openRemovePopup(tierKey, lineKey, cardEl) {
      const itemName = cardEl ? cardEl.dataset.giftTitle || '' : '';
      const priceText = cardEl
        ? (cardEl.querySelector('.current-price')?.textContent || '').trim()
        : '';
      const variantId = cardEl ? cardEl.dataset.variantId : '';
      this._pendingRemove = {
        tierKey,
        lineKey,
        itemName,
        priceText,
        variantId
      };

      const popup = document.querySelector('[data-cart-gift-remove-popup]');
      if (!popup) return;
      if (popup.parentElement !== document.body) {
        document.body.appendChild(popup);
      }
      popup.hidden = false;
      document.body.classList.add('cart-gift-popup-open');
    }

    _closeRemovePopup() {
      const popup = document.querySelector('[data-cart-gift-remove-popup]');
      if (popup) popup.hidden = true;
      document.body.classList.remove('cart-gift-popup-open');
      this._pendingRemove = null;
    }

    async _confirmRemove() {
      const pending = this._pendingRemove;
      if (!pending) return;
      this._closeRemovePopup();

      await this._setAttributes({
        [`free_gift_dismissed_${pending.tierKey}`]: '1'
      });
      await this._scheduleReconcile(0);

      window.removeCartItemEvent?.(
        'cart_element_remove_click',
        this._currentSurface(),
        pending.variantId,
        pending.itemName,
        pending.priceText,
        'remove_gift',
        window.buildProductExtras?.({}) || {}
      );
    }

    async _restoreGift(tierKey) {
      await this._setAttributes({ [`free_gift_dismissed_${tierKey}`]: '' });
      await this._scheduleReconcile(0);
    }

    _armSuppressGiftAddEvent(tierKey, variantId) {
      clearTimeout(this._suppressGiftAddEventTimer);
      this._suppressGiftAddEvent = { tierKey, variantId };
      this._suppressGiftAddEventTimer = setTimeout(() => {
        this._suppressGiftAddEvent = null;
      }, 5000);
    }

    _consumeSuppressGiftAddEvent(tierKey, variantId) {
      const marker = this._suppressGiftAddEvent;
      const matches =
        marker && marker.tierKey === tierKey && marker.variantId === variantId;
      if (matches) {
        clearTimeout(this._suppressGiftAddEventTimer);
        this._suppressGiftAddEvent = null;
      }
      return matches;
    }

    _scheduleReconcile(delay = 200) {
      if (!this._active) return Promise.resolve();
      clearTimeout(this._debounceTimer);
      if (!this._reconcileWaiters) this._reconcileWaiters = [];
      return new Promise(resolve => {
        this._reconcileWaiters.push(resolve);
        this._debounceTimer = setTimeout(() => {
          const waiters = this._reconcileWaiters;
          this._reconcileWaiters = [];
          this._reconcile().finally(() => {
            waiters.forEach(waiter => waiter());
          });
        }, delay);
      });
    }

    _reconcile() {
      if (!this._active) return Promise.resolve();
      if (this._busy) {
        this._dirty = true;
        return Promise.resolve();
      }
      this._busy = true;
      this._reconciling = this._runReconcile();
      return this._reconciling;
    }

    async _runReconcile() {
      let didMutate = false;

      try {
        let cart = await this._fetchCartJson();

        const validTierKeys = this._config.tiers.map(tier => tier.key);
        const orphanKeys = (cart.items || [])
          .filter(item => {
            const tierKey = item.properties && item.properties._free_gift;
            return tierKey && !validTierKeys.includes(tierKey);
          })
          .map(item => item.key);

        if (orphanKeys.length) {
          didMutate = true;
          this._showCartLoading();
          const html = await this._removeGiftLines(orphanKeys);
          if (html) this._applySectionHTML(html);
          cart = await this._fetchCartJson();
        }

        const desiredAttributes = {
          free_gift_config: this._buildFreeGiftConfig(),
          free_gift_markets: this._buildFreeGiftMarkets()
        };
        const configMatches = currentCart =>
          Object.keys(desiredAttributes).every(
            key =>
              (currentCart.attributes || {})[key] === desiredAttributes[key]
          );
        if (!configMatches(cart)) {
          await this._setAttributes(desiredAttributes);
          cart = await this._fetchCartJson();
        }

        let guard = 0;
        while (guard < 8 && this._active) {
          guard += 1;
          const desired = this._desiredState(cart);
          const actual = this._actualGiftLines(cart);
          const action = this._nextAction(desired, actual, configMatches(cart));
          if (!action) break;

          if (!didMutate) {
            didMutate = true;
            this._showCartLoading();
          }
          const html = await this._applyAction(action);
          if (html) this._applySectionHTML(html);
          cart = await this._fetchCartJson();
        }
        this._updateProgressBar(cart);
      } catch {
      } finally {
        if (didMutate) {
          try {
            const finalCart = await this._fetchCartJson();
            this._syncCartCounters(finalCart.item_count);
            if (document.body.dataset.template?.startsWith('cart')) {
              await window.cartPageRefreshSections?.();
              this._updateProgressBar(finalCart);
            }
            this._populateAllDropdowns();
          } catch {}
          this._hideCartLoading();
        }
        this._busy = false;
        if (this._dirty) {
          this._dirty = false;
          this._scheduleReconcile(0);
        }
      }
    }

    _syncCartCounters(itemCount) {
      const count = Number(itemCount) || 0;
      const bubble = document.querySelector('.cart-count-bubble');
      if (bubble) {
        const span =
          bubble.querySelector('[data-cart-count]') ||
          bubble.querySelector('span');
        if (span) span.textContent = count;
        bubble.style.removeProperty('display');
        bubble.classList.toggle('hidden', count === 0);
      }
      const headerCount = document.querySelector('.subtotal_text-count');
      if (headerCount) headerCount.classList.toggle('hidden', count === 0);
      if (count > 0) window.updateCartCount?.(count);
    }

    _qualifyingTotal(cart) {
      const giftTotal = (cart.items || []).reduce((sum, item) => {
        const isGift = item.properties && item.properties._free_gift;
        return isGift ? sum + item.final_line_price : sum;
      }, 0);
      return (cart.total_price || 0) - giftTotal;
    }

    _buildFreeGiftConfig() {
      const config = this._config;
      const markets =
        Array.isArray(config.markets) && config.markets.length
          ? config.markets
          : [config.market];
      const countries = markets
        .filter(Boolean)
        .map(marketCode => String(marketCode).toUpperCase());
      const currencies = config.currency ? [String(config.currency)] : [];
      const freeShipping = this._liveFreeShipping();
      const tiersByKey = {};
      (config.tiers || []).forEach(tier => {
        const options = tier.options || [];
        const defaultOption =
          options.find(option => option.productId === tier.defaultProductId) ||
          options[0];
        tiersByKey[tier.key] = {
          threshold: tier.amount / 100,
          title: (defaultOption && defaultOption.title) || '',
          variantIds: options.map(
            option => `gid://shopify/ProductVariant/${option.variantId}`
          )
        };
      });
      return JSON.stringify({
        countries,
        currencies,
        freeShipping,
        tiers: tiersByKey
      });
    }

    _liveFreeShipping() {
      const liveThreshold = Number(window.DELIVERY_FREE_SHIPPING_THRESHOLD);
      return liveThreshold > 0
        ? liveThreshold
        : this._config.freeShipping.amount / 100;
    }

    _buildFreeGiftMarkets() {
      const currentMarket = String(this._config.market || '').toUpperCase();
      const allMarkets = (this._config.allMarkets || []).map(marketEntry => {
        const tiersByKey = {};
        Object.keys(marketEntry.tiers || {}).forEach(tierKey => {
          const tier = marketEntry.tiers[tierKey];
          tiersByKey[tierKey] = {
            amount: tier.amount,
            title: tier.title,
            defaultVariantId: tier.defaultVariantId,
            variantIds: tier.variantIds
          };
        });
        return {
          countries: marketEntry.countries,
          currency: marketEntry.currency,
          freeShippingAmount: (marketEntry.countries || []).includes(
            currentMarket
          )
            ? this._liveFreeShipping()
            : marketEntry.freeShippingAmount,
          tiers: tiersByKey
        };
      });
      return JSON.stringify({ formatVersion: 1, markets: allMarkets });
    }

    _didGiftMarketChange(currentMarket, isGift) {
      let raw;
      try {
        raw = window.sessionStorage.getItem(GIFT_MARKET_SESSION_KEY);
      } catch (err) {
        return false;
      }
      if (!raw) return false;
      let last;
      try {
        last = JSON.parse(raw);
      } catch (err) {
        return false;
      }
      if (!last || typeof last.market === 'undefined') return false;
      if (!last.isGift && !isGift) return false;
      return last.market !== currentMarket;
    }

    _rememberCurrentMarket(currentMarket, isGift) {
      try {
        window.sessionStorage.setItem(
          GIFT_MARKET_SESSION_KEY,
          JSON.stringify({ market: currentMarket, isGift: !!isGift })
        );
      } catch (err) {}
    }

    _readMarketWarningRecord() {
      let raw;
      try {
        raw = window.sessionStorage.getItem(GIFT_MARKET_WARNING_KEY);
      } catch (err) {
        return null;
      }
      if (!raw) return null;
      try {
        const record = JSON.parse(raw);
        if (!record || typeof record.market === 'undefined') return null;
        return record;
      } catch (err) {
        return null;
      }
    }

    _writeMarketWarningRecord(record) {
      try {
        window.sessionStorage.setItem(
          GIFT_MARKET_WARNING_KEY,
          JSON.stringify(record)
        );
      } catch (err) {}
    }

    _clearMarketWarningRecord() {
      try {
        window.sessionStorage.removeItem(GIFT_MARKET_WARNING_KEY);
      } catch (err) {}
    }

    _initMarketWarning(market, isGift) {
      const changed = this._didGiftMarketChange(market, isGift);
      this._rememberCurrentMarket(market, isGift);

      let record = this._readMarketWarningRecord();
      if (record && (record.market !== market || record.seen === true)) {
        this._clearMarketWarningRecord();
        record = null;
      }

      if (changed) {
        record = { market, seen: false };
        this._writeMarketWarningRecord(record);
      }

      this._marketWarningMarket = market;
      this._marketWarningActive = !!record;
      this._marketWarningSeen = !!(record && record.seen);
      this._marketWarningDwellTimer = null;
      this._marketWarningReapplyTimer = null;
      this._onMarketWarningCartEvent =
        this._onMarketWarningCartEvent.bind(this);

      if (!this._marketWarningActive) return;

      this._applyMarketWarningState();
      this._observeMarketWarningDom();
      MARKET_WARNING_EVENTS.forEach(name =>
        document.addEventListener(name, this._onMarketWarningCartEvent)
      );
    }

    _observeMarketWarningDom() {
      if (this._marketWarningDomObserver) return;

      const hosts = [
        document.querySelector('.slide-cart-modal'),
        document.getElementById('cart-progress-zone')
      ].filter(Boolean);

      if (!hosts.length) return;

      this._marketWarningDomObserver = new MutationObserver(() => {
        clearTimeout(this._marketWarningReapplyTimer);
        this._marketWarningReapplyTimer = setTimeout(() => {
          this._applyMarketWarningState();
        }, 30);
      });

      hosts.forEach(host => {
        this._marketWarningDomObserver.observe(host, {
          childList: true,
          subtree: true
        });
      });
    }

    _ensureMarketWarningIntersectionObserver() {
      if (this._marketWarningIntersectionObserver) {
        return this._marketWarningIntersectionObserver;
      }
      if (typeof IntersectionObserver === 'undefined') return null;

      this._marketWarningVisibleEls = new Set();
      this._marketWarningIntersectionObserver = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (
              entry.isIntersecting &&
              entry.intersectionRatio >= 0.5 &&
              entry.target.isConnected
            ) {
              this._marketWarningVisibleEls.add(entry.target);
            } else {
              this._marketWarningVisibleEls.delete(entry.target);
            }
          });
          for (const el of [...this._marketWarningVisibleEls]) {
            if (!el.isConnected) this._marketWarningVisibleEls.delete(el);
          }

          if (!this._marketWarningVisibleEls.size) {
            clearTimeout(this._marketWarningDwellTimer);
            this._marketWarningDwellTimer = null;
            return;
          }
          if (this._marketWarningSeen || this._marketWarningDwellTimer) return;
          this._marketWarningDwellTimer = setTimeout(() => {
            this._marketWarningDwellTimer = null;
            this._markMarketWarningSeen();
          }, MARKET_WARNING_SEEN_DWELL_MS);
        },
        { threshold: 0.5 }
      );

      return this._marketWarningIntersectionObserver;
    }

    _markMarketWarningSeen() {
      if (!this._marketWarningActive || this._marketWarningSeen) return;
      this._marketWarningSeen = true;
      this._writeMarketWarningRecord({
        market: this._marketWarningMarket,
        seen: true
      });
      if (this._marketWarningIntersectionObserver) {
        this._marketWarningIntersectionObserver.disconnect();
      }
    }

    _onMarketWarningCartEvent() {
      if (!this._marketWarningActive || !this._marketWarningSeen) return;
      this._dismissMarketWarning();
    }

    _dismissMarketWarning() {
      this._marketWarningActive = false;
      this._marketWarningSeen = false;
      this._clearMarketWarningRecord();
      clearTimeout(this._marketWarningDwellTimer);
      this._marketWarningDwellTimer = null;
      clearTimeout(this._marketWarningReapplyTimer);
      this._marketWarningReapplyTimer = null;
      if (this._marketWarningVisibleEls) {
        this._marketWarningVisibleEls.clear();
      }

      document
        .querySelectorAll('[data-cart-gift-market-warning]')
        .forEach(warningEl => {
          warningEl.hidden = true;
        });

      if (this._marketWarningIntersectionObserver) {
        this._marketWarningIntersectionObserver.disconnect();
        this._marketWarningIntersectionObserver = null;
      }
      if (this._marketWarningDomObserver) {
        this._marketWarningDomObserver.disconnect();
        this._marketWarningDomObserver = null;
      }
      if (this._onMarketWarningCartEvent) {
        MARKET_WARNING_EVENTS.forEach(name =>
          document.removeEventListener(name, this._onMarketWarningCartEvent)
        );
      }
    }

    _applyMarketWarningState() {
      if (!this._marketWarningActive) return;
      const observer = this._ensureMarketWarningIntersectionObserver();
      const warnings = document.querySelectorAll(
        '[data-cart-gift-market-warning]'
      );
      warnings.forEach(warningEl => {
        warningEl.hidden = false;
        if (observer && !this._marketWarningSeen) {
          observer.observe(warningEl);
        }
      });
      if (
        !observer &&
        warnings.length &&
        !this._marketWarningSeen &&
        !this._marketWarningDwellTimer
      ) {
        this._marketWarningDwellTimer = setTimeout(() => {
          this._marketWarningDwellTimer = null;
          this._markMarketWarningSeen();
        }, MARKET_WARNING_SEEN_DWELL_MS);
      }
    }

    _desiredState(cart) {
      const total = this._qualifyingTotal(cart);
      const attrs = cart.attributes || {};

      return this._config.tiers.map(tier => {
        const dismissed = attrs[`free_gift_dismissed_${tier.key}`] === '1';
        const met = total >= tier.amount;
        const choiceHandle = attrs[`free_gift_choice_${tier.key}`];
        const byChoice = choiceHandle
          ? tier.options.find(
              option => option.handle === choiceHandle && option.available
            )
          : null;
        const option =
          byChoice ||
          tier.options.find(
            option =>
              option.productId === tier.defaultProductId && option.available
          ) ||
          tier.options.find(option => option.available) ||
          null;
        return { tier, active: met && !dismissed, option };
      });
    }

    _actualGiftLines(cart) {
      const linesByTierKey = {};
      (cart.items || []).forEach(item => {
        const tierKey = item.properties && item.properties._free_gift;
        if (!tierKey) return;
        (linesByTierKey[tierKey] = linesByTierKey[tierKey] || []).push(item);
      });
      return linesByTierKey;
    }

    _nextAction(desiredStates, actualLinesByTierKey, canAdd) {
      for (const desiredState of desiredStates) {
        const lines = actualLinesByTierKey[desiredState.tier.key] || [];

        if (lines.length > 1) {
          return { type: 'remove', lineKey: lines[lines.length - 1].key };
        }

        const primary = lines[0];
        if (desiredState.active) {
          if (!primary) {
            if (desiredState.option && canAdd)
              return {
                type: 'add',
                variantId: desiredState.option.variantId,
                tierKey: desiredState.tier.key
              };
          } else if (
            !desiredState.option ||
            Number(primary.variant_id) !== desiredState.option.variantId ||
            primary.quantity !== 1
          ) {
            return { type: 'remove', lineKey: primary.key };
          }
        } else if (primary) {
          return { type: 'remove', lineKey: primary.key };
        }
      }
      return null;
    }

    async _applyAction(action) {
      if (action.type === 'add')
        return this._cartAdd(action.variantId, action.tierKey);
      if (action.type === 'remove')
        return this._cartRemoveByKey(action.lineKey);
      return null;
    }

    async _fetchCartJson() {
      const res = await fetch(`${localeRoot()}/cart.js`, { cache: 'no-store' });
      return res.json();
    }

    async _cartAdd(variantId, tierKey) {
      try {
        // Shared transport from assets/add-to-cart.js: on a page where Amplify
        // blocks cart writes it routes through an unpatched fetch, otherwise it is
        // a plain fetch.
        const addFetch = window.cartAddFetch || fetch;
        const res = await addFetch(`${localeRoot()}/cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
                properties: { _free_gift: tierKey }
              }
            ],
            sections: ['slide-cart-fragment'],
            sections_url: sectionsUrl()
          })
        });
        if (!res.ok) {
          return null;
        }
        const json = await res.json();
        // Amplify's fabricated interception payload (see assets/add-to-cart.js) is
        // a 200 with no `sections` — bail before the analytics event below fires
        // for a gift that was never actually added.
        if (window.isCartAddIntercepted?.(json)) {
          return null;
        }
        const html = json.sections && json.sections['slide-cart-fragment'];

        const tier = this._config.tiers.find(
          candidateTier => candidateTier.key === tierKey
        );
        const option =
          tier &&
          tier.options.find(
            candidateOption => candidateOption.variantId === variantId
          );
        const suppressed = this._consumeSuppressGiftAddEvent(
          tierKey,
          variantId
        );
        const addedLine = (json.items || []).find(
          line => String(line.variant_id) === String(variantId)
        );
        const price =
          option && addedLine && !suppressed
            ? await this._giftLinePrice(addedLine, tierKey)
            : NaN;
        if (Number.isFinite(price)) {
          window.addToCartEvent?.(
            1,
            this._currentSurface(),
            String(option.variantId),
            (price / 100).toFixed(2),
            option.title,
            option.url,
            '',
            { type: 'gift' }
          );
        }

        return html || null;
      } catch (err) {
        return null;
      }
    }

    async _giftLinePrice(addedLine, tierKey) {
      if (Number.isFinite(addedLine.final_price)) return addedLine.final_price;
      try {
        const cart = await this._fetchCartJson();
        const line = (cart.items || []).find(
          item =>
            String(item.variant_id) === String(addedLine.variant_id) &&
            item.properties?._free_gift === tierKey
        );
        return line ? line.final_price : NaN;
      } catch {
        return NaN;
      }
    }

    async _cartRemoveByKey(lineKey) {
      try {
        const res = await fetch(`${localeRoot()}/cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            updates: { [lineKey]: 0 },
            sections: ['slide-cart-fragment'],
            sections_url: sectionsUrl()
          })
        });
        if (!res.ok) {
          return null;
        }
        const json = await res.json();
        return (json.sections && json.sections['slide-cart-fragment']) || null;
      } catch (err) {
        return null;
      }
    }

    async _removeGiftLines(lineKeys, attributes) {
      if (!Array.isArray(lineKeys) || !lineKeys.length) return null;
      const updates = {};
      lineKeys.forEach(key => {
        updates[key] = 0;
      });
      try {
        const res = await fetch(`${localeRoot()}/cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            updates,
            ...(attributes ? { attributes } : {}),
            sections: ['slide-cart-fragment'],
            sections_url: sectionsUrl()
          })
        });
        if (!res.ok) {
          return null;
        }
        const json = await res.json();
        return (json.sections && json.sections['slide-cart-fragment']) || null;
      } catch (err) {
        return null;
      }
    }

    _purgeAllGiftLines() {
      if (!this._purging) {
        this._purging = this._runPurge().finally(() => {
          this._purging = null;
        });
      }
      return this._purging;
    }

    async _runPurge() {
      try {
        const cart = await this._fetchCartJson();
        const giftLineKeys = (cart.items || [])
          .filter(item => item.properties && item.properties._free_gift)
          .map(item => item.key);
        const attrs = cart.attributes || {};
        const attributes = GIFT_ATTRIBUTE_KEYS.filter(key => attrs[key]).reduce(
          (acc, key) => ({ ...acc, [key]: '' }),
          {}
        );
        const hasAttributes = Object.keys(attributes).length > 0;

        if (!giftLineKeys.length && !hasAttributes) return;

        this._showCartLoading();
        if (giftLineKeys.length) {
          const html = await this._removeGiftLines(
            giftLineKeys,
            hasAttributes ? attributes : null
          );
          if (html) this._applySectionHTML(html);
        } else {
          await this._setAttributes(attributes);
        }

        const finalCart = await this._fetchCartJson();
        this._syncCartCounters(finalCart.item_count);
        if (document.body.dataset.template?.startsWith('cart')) {
          await window.cartPageRefreshSections?.();
        }
        this._applyMarketWarningState();
      } catch {
      } finally {
        this._hideCartLoading();
      }
    }

    async _setAttributes(attributes) {
      try {
        const res = await fetch(`${localeRoot()}/cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes,
            sections: ['slide-cart-fragment'],
            sections_url: sectionsUrl()
          })
        });
        if (!res.ok) {
          return null;
        }
        const json = await res.json();
        const html =
          (json.sections && json.sections['slide-cart-fragment']) || null;
        if (html) this._applySectionHTML(html);
        return html;
      } catch (err) {
        return null;
      }
    }

    _applySectionHTML(html) {
      const drawer = document.querySelector('slide-cart-drawer');
      if (drawer && typeof drawer.updateFromSectionHTML === 'function') {
        try {
          drawer.updateFromSectionHTML(html);
        } catch {}
      }
    }

    _updateProgressBar(cart) {
      this._applyMarketWarningState();
      document
        .querySelectorAll('cart-gift-progress')
        .forEach(root => this._updateProgressBarFor(root, cart));
    }

    _updateProgressBarFor(root, cart) {
      if (!root) return;

      const points = [...root.querySelectorAll('[data-cart-gift-point]')].map(
        pointEl => ({
          el: pointEl,
          amount: Number(pointEl.dataset.amount) || 0,
          kind: pointEl.dataset.kind,
          title: pointEl.dataset.giftTitle || ''
        })
      );
      if (!points.length) return;

      const bar = this._computeBar(points, {
        shipping: cart.items_subtotal_price || 0,
        gift: this._qualifyingTotal(cart)
      });

      const lines = root.querySelectorAll('[data-cart-gift-line]');
      points.forEach((point, index) => {
        const reached = index < bar.reached;
        const fill = lines[index]?.querySelector('[data-cart-gift-fill]');
        if (fill) {
          const width = reached
            ? 100
            : index === bar.reached
              ? bar.progress
              : 0;
          fill.style.width = `${width}%`;
        }
        point.el.dataset.active = String(reached);
      });

      const dot = root.querySelector('[data-cart-gift-dot]');
      if (dot) {
        dot.dataset.filled = String(bar.reached > 0 || bar.progress > 0);
      }

      this._updateProgressMessage(root, bar, points.length);
    }

    _computeBar(points, bases) {
      const gifts = points.filter(point => point.kind === 'gift');
      let reached = 0;
      let giftsReached = 0;
      let prevAmount = 0;
      let next = null;
      let lastGiftTitle = '';
      let nextGiftTitle = '';

      points.forEach(point => {
        const base = point.kind === 'shipping' ? bases.shipping : bases.gift;
        if (!next && base >= point.amount) {
          reached += 1;
          prevAmount = point.amount;
          if (point.kind === 'gift') {
            giftsReached += 1;
            lastGiftTitle = point.title;
          }
          return;
        }
        if (!next) next = { ...point, base };
        if (point.kind === 'gift' && !nextGiftTitle)
          nextGiftTitle = point.title;
      });

      let progress = 100;
      let amount = '';
      if (next) {
        const span = next.amount - prevAmount;
        progress =
          span > 0
            ? Math.min(
                100,
                Math.max(0, Math.floor(((next.base - prevAmount) * 100) / span))
              )
            : 0;
        amount = formatCents(next.amount - next.base);
      }

      let giftTitle = giftsReached ? lastGiftTitle : gifts[0]?.title || '';
      const hasShipping = points.some(point => point.kind === 'shipping');
      let labelKey = '';
      if (next) {
        if (next.kind === 'shipping') labelKey = 'before_shipping_html';
        else if (!reached) labelKey = 'before_first_gift';
        else if (!giftsReached) labelKey = 'shipping_unlocked_next_gift';
        else labelKey = 'gift_unlocked_next_gift';
      } else if (gifts.length === 2) {
        giftTitle = gifts[0].title;
        nextGiftTitle = gifts[1].title;
        labelKey = hasShipping ? 'all_unlocked' : 'all_unlocked_no_shipping';
      } else if (gifts.length === 1) {
        labelKey = hasShipping
          ? 'all_unlocked_single'
          : 'all_unlocked_single_no_shipping';
      }

      return {
        reached,
        progress,
        labelKey,
        amount,
        giftTitle,
        nextGiftTitle
      };
    }

    _updateProgressMessage(root, bar, pointCount) {
      const i18nEl = root.querySelector('[data-cart-gift-i18n]');
      const messageEl = root.querySelector('[data-cart-gift-message]');
      const rowEl = root.querySelector('[data-cart-gift-row]');
      if (!i18nEl || !messageEl) return;

      let i18n;
      try {
        i18n = JSON.parse(i18nEl.textContent);
      } catch (err) {
        return;
      }

      const text = decodeEntities(
        fillTemplate(i18n[bar.labelKey], {
          amount: bar.amount,
          gift: bar.giftTitle,
          next_gift: bar.nextGiftTitle,
          gift_1: bar.giftTitle,
          gift_2: bar.nextGiftTitle
        })
      );
      if (messageEl.textContent !== text) messageEl.textContent = text;

      if (!rowEl) return;
      if (!text) {
        [
          'role',
          'aria-label',
          'aria-valuemin',
          'aria-valuemax',
          'aria-valuenow'
        ].forEach(name => rowEl.removeAttribute(name));
        rowEl.setAttribute('aria-hidden', 'true');
        return;
      }
      const overall = Math.min(
        100,
        Math.floor((bar.reached * 100 + bar.progress) / pointCount)
      );
      rowEl.removeAttribute('aria-hidden');
      rowEl.setAttribute('role', 'progressbar');
      rowEl.setAttribute('aria-label', text);
      rowEl.setAttribute('aria-valuemin', '0');
      rowEl.setAttribute('aria-valuemax', '100');
      rowEl.setAttribute('aria-valuenow', String(overall));
    }

    _currentSurface() {
      return document.body.dataset.template?.startsWith('cart')
        ? 'cart_page'
        : 'slide_cart';
    }

    _loadingHost() {
      if (document.body.dataset.template?.startsWith('cart')) {
        return (
          document.getElementById('main-cart-items') ||
          document.querySelector('.cart_items_wr') ||
          document.querySelector('.slide-cart-modal')
        );
      }
      return document.querySelector('.slide-cart-modal');
    }

    _ensureLoadingOverlay() {
      const modal = this._loadingHost();
      if (!modal) return null;
      let overlay = modal.querySelector('[data-cart-gift-loading]');
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.className = 'cart-gift-loading';
        overlay.setAttribute('data-cart-gift-loading', '');
        overlay.hidden = true;
        overlay.innerHTML = `<span class="cart-gift-loading__spinner">
          <svg aria-hidden="true" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
            <circle class="path" stroke-width="6" fill="none" cx="33" cy="33" r="30"></circle>
          </svg>
        </span>`;
        if (getComputedStyle(modal).position === 'static') {
          modal.style.position = 'relative';
        }
        modal.appendChild(overlay);
      }
      return overlay;
    }

    _showCartLoading() {
      const overlay = this._ensureLoadingOverlay();
      if (overlay) overlay.hidden = false;
    }

    _hideCartLoading() {
      document.querySelectorAll('[data-cart-gift-loading]').forEach(overlay => {
        overlay.hidden = true;
      });
    }
  }

  if (!customElements.get('cart-gift-controller')) {
    customElements.define('cart-gift-controller', CartGiftController);
  }
})();
